import React, { useMemo, useState } from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type { ParityChainDailyPoint, ParityChainSummary } from '~/types/services/priceParrot';

import { PARITY_SECTION_BLOCK_MB, SECTION_SX } from '../PriceParrotParityTool/constants';

import DateRangeRow from './shared/DateRangeRow';
import FilterPillRow, { PillOption } from './shared/FilterPillRow';
import KpiTile from './shared/KpiTile';
import { fmtInt, fmtPct, fmtShortDate } from './utils';

type Metric = 'pct_out_of_parity' | 'jobs_out_of_parity' | 'total_jobs';

const METRIC_OPTIONS: Array<{ value: Metric; label: string }> = [
  { value: 'pct_out_of_parity', label: 'Out-of-parity %' },
  { value: 'jobs_out_of_parity', label: 'Out-of-parity scrapes' },
  { value: 'total_jobs', label: 'Total scrapes' },
];

interface Props {
  daily: Array<ParityChainDailyPoint>;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  continent: string | null;
  continentOptions: Array<PillOption<string | null>>;
  onContinentChange: (next: string | null) => void;
  dateFrom: string;
  dateTo: string;
  minDate: string;
  maxDate: string;
  onDateFromChange: (next: string) => void;
  onDateToChange: (next: string) => void;
  onResetRange: () => void;
  summary: ParityChainSummary | null;
  summaryLoading: boolean;
}

function computeParityScore(pctOop: number | null | undefined): number | null {
  if (pctOop == null) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round((1 - pctOop) * 100)));
}

export default function DailyTrendChart({
  daily,
  isLoading,
  isError,
  errorMessage,
  continent,
  continentOptions,
  onContinentChange,
  dateFrom,
  dateTo,
  minDate,
  maxDate,
  onDateFromChange,
  onDateToChange,
  onResetRange,
  summary,
  summaryLoading,
}: Props) {
  const [metric, setMetric] = useState<Metric>('pct_out_of_parity');

  const rows = useMemo(() => {
    return daily.map((d) => ({
      report_date: d.report_date,
      value: d[metric],
    }));
  }, [daily, metric]);

  const parityScore = computeParityScore(summary?.pct_out_of_parity);

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Box sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Parity trend</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, maxWidth: 720 }}>
          Time series from historical Price Parrot data: share of qualifying checks where LE appeared overpriced vs. the
          best competitor. Filter by continent to compare the regional parity score to the overall chain score. Default
          range: last 12 months.
        </Typography>
      </Box>

      <FormControl size="small" sx={{ minWidth: 220, mb: PARITY_SECTION_BLOCK_MB }}>
        <InputLabel id="chain-trend-metric-label">Metric</InputLabel>
        <Select
          labelId="chain-trend-metric-label"
          label="Metric"
          value={metric}
          onChange={(e) => setMetric(e.target.value as Metric)}
        >
          {METRIC_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="flex-start"
        sx={{ mb: PARITY_SECTION_BLOCK_MB }}
      >
        <FilterPillRow<string | null>
          label="Continent"
          options={continentOptions}
          value={continent}
          onChange={onContinentChange}
        />

        <KpiTile
          label="Parity score"
          value={parityScore != null ? `${parityScore}%` : '—'}
          caption={`${continent ?? 'All continents'} · ${summary?.weighting ?? '—'}-weighted`}
          isLoading={summaryLoading}
        />
      </Stack>

      <Box sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
        <DateRangeRow
          dateFrom={dateFrom}
          dateTo={dateTo}
          minDate={minDate}
          maxDate={maxDate}
          onDateFromChange={onDateFromChange}
          onDateToChange={onDateToChange}
          onReset={onResetRange}
        />
      </Box>

      {isError && errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ position: 'relative', minHeight: 360 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No trend data for this chain.
          </Typography>
        )}
        {!isLoading && rows.length > 0 && (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="report_date"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickFormatter={(v) => fmtShortDate(String(v))}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) =>
                  metric === 'pct_out_of_parity' ? `${Math.round(Number(v) * 100)}%` : String(Math.round(Number(v)))
                }
              />
              <Tooltip
                labelFormatter={(label) => fmtShortDate(String(label))}
                formatter={(value: number | string) => {
                  if (value == null || value === '') {
                    return '—';
                  }
                  return metric === 'pct_out_of_parity' ? fmtPct(Number(value)) : fmtInt(Number(value));
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                name={METRIC_OPTIONS.find((m) => m.value === metric)?.label ?? ''}
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
}
